import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Edit2, Repeat, X, Copy, TrendingUp, TrendingDown } from 'lucide-react';
import OperationForm from './OperationForm';
import DeleteRecurringDialog from './DeleteRecurringDialog';
import EditRecurringDialog from './EditRecurringDialog';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const DayDetail = ({ date, calendarId, onOperationChanged, currency, onClose }) => {
  const [operations, setOperations] = useState([]);
  const [dailyBalance, setDailyBalance] = useState(null);
  const [showOperationForm, setShowOperationForm] = useState(false);
  const [editingOperation, setEditingOperation] = useState(null);
  const [deletingOperation, setDeletingOperation] = useState(null);
  const [editingRecurringOperation, setEditingRecurringOperation] = useState(null);
  const [editRecurringType, setEditRecurringType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    if (date && calendarId) {
      loadDayData();
    }
  }, [date, calendarId]);

  const loadDayData = async () => {
    setLoading(true);
    try {
      const [opsResponse, balanceResponse] = await Promise.all([
        axios.get(`${API}/operations?calendar_id=${calendarId}&date=${date}&include_projections=true`, { withCredentials: true }),
        axios.get(`${API}/balance/${date}?calendar_id=${calendarId}`, { withCredentials: true })
      ]);
      setOperations(opsResponse.data);
      setDailyBalance(balanceResponse.data);
    } catch (error) {
      // Erreur silencieuse en production
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOperation = async (operation) => {
    if (operation.recurring_id) {
      setDeletingOperation(operation);
    } else {
      try {
        await axios.delete(`${API}/operations/${operation.id}`, { withCredentials: true });
        toast.success('Opération supprimée');
        loadDayData();
        onOperationChanged();
      } catch (error) {
        console.error('Error deleting operation:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleDeleteRecurring = async (deleteType) => {
    if (!deletingOperation) return;
    
    try {
      await axios.post(
        `${API}/operations/${deletingOperation.id}/delete-recurring`,
        { delete_type: deleteType, operation_date: deletingOperation.date },
        { withCredentials: true }
      );
      
      // Messages de confirmation détaillés
      if (deleteType === 'this_only') {
        toast.success(
          <div className="space-y-1">
            <p className="font-medium">Suppression effectuée</p>
            <p className="text-sm opacity-80">Seule cette occurrence a été supprimée. Les autres restent inchangées.</p>
          </div>,
          { duration: 4000 }
        );
      } else if (deleteType === 'future') {
        toast.success(
          <div className="space-y-1">
            <p className="font-medium">Suppression effectuée</p>
            <p className="text-sm opacity-80">Cette occurrence et toutes les suivantes ont été supprimées. Les occurrences passées restent inchangées.</p>
          </div>,
          { duration: 4000 }
        );
      } else {
        toast.success(
          <div className="space-y-1">
            <p className="font-medium">Suppression effectuée</p>
            <p className="text-sm opacity-80">Toutes les occurrences de cette série ont été supprimées.</p>
          </div>,
          { duration: 4000 }
        );
      }
      
      setDeletingOperation(null);
      loadDayData();
      onOperationChanged();
    } catch (error) {
      console.error('Error deleting recurring operation:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEditOperation = (operation) => {
    // If operation is recurring, ask which occurrences to edit
    if (operation.recurring_id) {
      setEditingRecurringOperation(operation);
    } else {
      setEditingOperation(operation);
      setShowOperationForm(true);
    }
  };

  const handleEditRecurring = async (editType) => {
    if (!editingRecurringOperation) return;
    
    // Store the edit type for later use in the form submission
    setEditRecurringType(editType);
    setEditingOperation(editingRecurringOperation);
    setEditingRecurringOperation(null);
    setShowOperationForm(true);
  };

  const handleOperationFormClose = async (savedWithRecurringType = null) => {
    setShowOperationForm(false);
    setEditingOperation(null);
    setEditRecurringType(null);
    await loadDayData();
    onOperationChanged();
  };

  const handleDuplicateOperation = async (operation) => {
    try {
      const duplicateData = {
        calendar_id: calendarId,
        date: operation.date,
        type: operation.type,
        amount: operation.amount,
        category: operation.category,
        payment_method: operation.payment_method || '',
        account: operation.account || '',
        comment: operation.comment ? `${operation.comment} (copie)` : 'Copie'
      };

      await axios.post(`${API}/operations?calendar_id=${calendarId}`, duplicateData, { withCredentials: true });
      toast.success('Opération dupliquée');
      await loadDayData();
      onOperationChanged();
    } catch (error) {
      console.error('Error duplicating operation:', error);
      toast.error('Erreur lors de la duplication');
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const calculateStartBalance = () => {
    if (!dailyBalance || !operations.length) return dailyBalance?.balance || 0;
    
    let startBalance = dailyBalance.balance;
    operations.forEach(op => {
      if (op.type === 'credit') startBalance -= op.amount;
      else startBalance += op.amount;
    });
    return startBalance;
  };

  const filteredOperations = operations.filter(op => {
    if (filterType === 'all') return true;
    if (filterType === 'credit') return op.type === 'credit';
    if (filterType === 'debit') return op.type === 'debit';
    if (filterType === 'recurring') return op.recurring_id;
    return true;
  });

  const startBalance = calculateStartBalance();
  const endBalance = dailyBalance?.balance || 0;
  const totalCredits = operations.filter(o => o.type === 'credit').reduce((s, o) => s + o.amount, 0);
  const totalDebits = operations.filter(o => o.type === 'debit').reduce((s, o) => s + o.amount, 0);

  // If showing operation form or delete dialog, render only that
  if (showOperationForm) {
    return (
      <OperationForm
        open={true}
        onClose={() => {
          setShowOperationForm(false);
          setEditingOperation(null);
          setEditRecurringType(null);
        }}
        onSuccess={async () => {
          await loadDayData();
          onOperationChanged();
        }}
        date={date}
        calendarId={calendarId}
        operation={editingOperation}
        currency={currency}
        editRecurringType={editRecurringType}
      />
    );
  }

  if (deletingOperation) {
    return (
      <DeleteRecurringDialog
        open={true}
        onClose={() => setDeletingOperation(null)}
        onDelete={handleDeleteRecurring}
      />
    );
  }

  if (editingRecurringOperation) {
    return (
      <EditRecurringDialog
        open={true}
        onClose={() => setEditingRecurringOperation(null)}
        onEdit={handleEditRecurring}
        operationName={editingRecurringOperation.category || editingRecurringOperation.comment || 'Opération'}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <motion.div 
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />
      
      {/* Content - Premium Drawer */}
      <motion.div 
        className="relative w-full max-w-md max-h-[90vh] bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      >
        {/* Header - Premium */}
        <div className="bg-foreground text-white p-5 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold capitalize tracking-tight">{formatDate(date)}</h2>
              <p className="text-xs text-white/60 mt-0.5">{operations.length} opération{operations.length > 1 ? 's' : ''}</p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-white/10 transition-colors"
            >
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Balance summary - Premium Minimal */}
        <div className="p-4 bg-muted/30 border-b border-border/50 grid grid-cols-4 gap-3 text-center">
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Début</div>
            <div className="text-sm font-semibold text-foreground mt-0.5">{startBalance.toFixed(0)}€</div>
          </div>
          <div>
            <div className="text-[10px] text-emerald-600 uppercase tracking-wider">Entrées</div>
            <div className="text-sm font-semibold text-emerald-600 mt-0.5">+{totalCredits.toFixed(0)}€</div>
          </div>
          <div>
            <div className="text-[10px] text-rose-500 uppercase tracking-wider">Sorties</div>
            <div className="text-sm font-semibold text-rose-500 mt-0.5">-{totalDebits.toFixed(0)}€</div>
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Fin</div>
            <div className={`text-sm font-semibold mt-0.5 ${endBalance >= 0 ? 'text-primary' : 'text-rose-500'}`}>
              {endBalance.toFixed(0)}€
            </div>
          </div>
        </div>

        {/* Add button - Premium */}
        <div className="p-4 border-b border-border/50">
          <Button
            onClick={() => setShowOperationForm(true)}
            className="w-full gap-2 premium-btn-primary h-12 text-sm font-medium"
            data-testid="add-operation-button"
          >
            <Plus className="h-5 w-5" strokeWidth={2} />
            Ajouter une opération
          </Button>
        </div>

        {/* Filters - Premium Pills */}
        <div className="px-4 py-3 flex gap-2 overflow-x-auto border-b border-border/50 bg-white scrollbar-hide">
          {[
            { key: 'all', label: 'Tout', count: operations.length },
            { key: 'credit', label: 'Entrées', count: operations.filter(o => o.type === 'credit').length, color: 'emerald' },
            { key: 'debit', label: 'Sorties', count: operations.filter(o => o.type === 'debit').length, color: 'rose' },
            { key: 'recurring', label: 'Récurrent', count: operations.filter(o => o.recurring_id).length, color: 'primary' }
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilterType(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                filterType === f.key 
                  ? f.color === 'emerald' ? 'bg-emerald-500 text-white' :
                    f.color === 'rose' ? 'bg-rose-400 text-white' :
                    f.color === 'primary' ? 'bg-primary text-white' :
                    'bg-foreground text-white'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {f.label} ({f.count})
            </button>
          ))}
        </div>

        {/* Operations list - Premium Cards */}
        <div className="overflow-y-auto scrollbar-hide" style={{ maxHeight: 'calc(90vh - 320px)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <motion.div 
                className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
            </div>
          ) : filteredOperations.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <div className="text-4xl mb-3 opacity-50">📭</div>
              <p className="text-sm">Aucune opération</p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              <AnimatePresence>
                {filteredOperations.map((op, index) => (
                  <motion.div
                    key={op.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="premium-card !p-4"
                    data-testid={`operation-item-${op.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        op.type === 'credit' 
                          ? 'bg-emerald-100' 
                          : op.savings_goal_id 
                            ? 'bg-accent/20' 
                            : 'bg-rose-100'
                      }`}>
                        {op.type === 'credit' ? (
                          <TrendingUp className="h-5 w-5 text-emerald-600" strokeWidth={1.5} />
                        ) : op.savings_goal_id ? (
                          <span className="text-lg">🎯</span>
                        ) : (
                          <TrendingDown className="h-5 w-5 text-rose-500" strokeWidth={1.5} />
                        )}
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-foreground truncate">{op.category}</span>
                          {op.recurring_id && (
                            <span className="px-1.5 py-0.5 bg-primary/10 text-primary text-[10px] rounded-full flex items-center gap-0.5 font-medium">
                              <Repeat className="h-2.5 w-2.5" /> Auto
                            </span>
                          )}
                          {op.savings_goal_id && (
                            <span className="px-1.5 py-0.5 bg-accent/20 text-accent text-[10px] rounded-full font-medium">
                              Épargne
                            </span>
                          )}
                          {op.occurrence_number && op.total_occurrences && (
                            <span className="px-1.5 py-0.5 bg-muted text-muted-foreground text-[10px] rounded-full font-medium">
                              {op.occurrence_number}/{op.total_occurrences}
                            </span>
                          )}
                        </div>
                        {op.comment && (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{op.comment}</p>
                        )}
                      </div>

                      {/* Amount */}
                      <div className={`text-right font-semibold ${
                        op.type === 'credit' ? 'text-emerald-600' : 'text-rose-500'
                      }`}>
                        {op.type === 'credit' ? '+' : '-'}{op.amount.toFixed(0)}€
                      </div>
                    </div>

                    {/* Actions - Clean */}
                    <div className="flex gap-1 mt-3 pt-3 border-t border-border/50">
                      <button
                        onClick={() => handleEditOperation(op)}
                        className="flex-1 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors flex items-center justify-center gap-1.5 font-medium"
                      >
                        <Edit2 className="h-3.5 w-3.5" strokeWidth={1.5} /> Modifier
                      </button>
                      <button
                        onClick={() => handleDuplicateOperation(op)}
                        className="flex-1 py-2 text-xs text-muted-foreground hover:text-primary hover:bg-primary/5 rounded-lg transition-colors flex items-center justify-center gap-1.5 font-medium"
                      >
                        <Copy className="h-3.5 w-3.5" strokeWidth={1.5} /> Dupliquer
                      </button>
                      <button
                        onClick={() => handleDeleteOperation(op)}
                        className="flex-1 py-2 text-xs text-muted-foreground hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors flex items-center justify-center gap-1.5 font-medium"
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /> Supprimer
                      </button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default DayDetail;
